import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Calendar, Link2, Check, X, Loader2 } from "lucide-react";

const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function AvailabilityManager({ userId }) {
  const [slots, setSlots] = useState([]);
  const [duration, setDuration] = useState(50);
  const [gcal, setGcal] = useState({ enabled: false, connected: false, message: "" });
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!userId) return;
    api.get(`/availability/${userId}`).then((r) => {
      const withKeys = (r.data.slots || []).map((s, i) => ({ ...s, __key: `${i}-${s.weekday}-${s.start_time}` }));
      setSlots(withKeys);
      setDuration(r.data.slot_duration_min || 50);
    });
    api.get("/gcal/status").then((r) => setGcal(r.data)).catch(() => {});
  }, [userId]);

  const add = () => setSlots((prev) => [...prev, { __key: Date.now() + Math.random(), weekday: 0, start_time: "09:00", end_time: "12:00" }]);
  const upd = (i, k, v) => setSlots((prev) => prev.map((s, ix) => ix === i ? { ...s, [k]: k === "weekday" ? parseInt(v) : v } : s));
  const rm = (i) => setSlots((prev) => prev.filter((_, ix) => ix !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = slots.map(({ __key, ...rest }) => rest);
      await api.put("/availability", { slots: payload, slot_duration_min: duration });
      toast.success("Horários salvos");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const connectGcal = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/gcal/auth-url");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setConnecting(false);
    }
  };

  const disconnectGcal = async () => {
    await api.post("/gcal/disconnect");
    toast.success("Google Agenda desconectado");
    api.get("/gcal/status").then((r) => setGcal(r.data));
  };

  return (
    <Card className="card-soft" data-testid="availability-manager">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-semibold text-navy-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-teal-600" />Meus horários</h3>
            <p className="text-sm text-slate-600 mt-1">Defina sua agenda semanal. Pacientes verão só horários livres.</p>
          </div>
          {/* Google Calendar status */}
          <div className="text-right">
            {gcal.connected ? (
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-3 py-1 text-xs">
                <Check className="w-3.5 h-3.5" />
                Google Agenda: {gcal.google_email}
                <button className="ml-1 hover:text-red-600" onClick={disconnectGcal} data-testid="gcal-disconnect"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={connectGcal} disabled={connecting} data-testid="gcal-connect" className="border-navy-900 text-navy-900">
                {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                {gcal.enabled ? "Conectar Google Agenda" : "Google Agenda pendente"}
              </Button>
            )}
            <div className="text-[11px] text-slate-500 mt-1 max-w-[220px] text-right">{gcal.message}</div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {slots.length === 0 && <p className="text-sm text-slate-500">Nenhum horário definido. Adicione janelas semanais abaixo.</p>}
          {slots.map((s, i) => (
            <div key={s.__key} className="flex flex-wrap items-center gap-2 p-3 border border-slate-200 rounded-xl bg-slate-50/50" data-testid={`avail-row-${i}`}>
              <Select value={String(s.weekday)} onValueChange={(v) => upd(i, "weekday", v)}>
                <SelectTrigger className="w-36" data-testid={`avail-day-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d, idx) => <SelectItem key={d} value={String(idx)}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="time" value={s.start_time} onChange={(e)=>upd(i, "start_time", e.target.value)} className="w-32" data-testid={`avail-start-${i}`} />
              <span className="text-slate-400">até</span>
              <Input type="time" value={s.end_time} onChange={(e)=>upd(i, "end_time", e.target.value)} className="w-32" data-testid={`avail-end-${i}`} />
              <button className="ml-auto text-red-500 hover:text-red-700" onClick={()=>rm(i)} data-testid={`avail-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={add} data-testid="avail-add"><Plus className="w-4 h-4 mr-1" />Adicionar janela</Button>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Duração por consulta</Label>
            <Select value={String(duration)} onValueChange={(v)=>setDuration(parseInt(v))}>
              <SelectTrigger className="w-28" data-testid="avail-duration"><SelectValue /></SelectTrigger>
              <SelectContent>{[30, 45, 50, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={busy} className="bg-teal-500 hover:bg-teal-600 ml-auto" data-testid="avail-save">{busy ? "Salvando..." : "Salvar agenda"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
